// src/components/interactive/QuizComponent.js - Interactive quiz to test knowledge of PHI email handling
import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '../common/Button';
import { motion } from 'framer-motion';

const QuizContainer = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: var(--spacing-lg);
`;

const QuizHeader = styled.div`
  background-color: var(--primary-color);
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const QuizTitle = styled.h4`
  margin: 0;
  font-size: 1.2rem;
`;

const QuizContent = styled.div`
  padding: var(--spacing-lg);
  background-color: var(--background-color);
  color: var(--text-color);
`;

const Question = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const QuestionText = styled.h3`
  font-size: 1.1rem;
  margin-bottom: var(--spacing-md);
  color: var(--text-color);
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const Option = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'var(--background-color)'};
  border-color: ${props => props.selected ? 'var(--primary-color)' : 'var(--border-color)'};
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const OptionInput = styled.input`
  margin-top: 3px;
`;

const OptionLabel = styled.label`
  cursor: pointer;
  flex: 1;
  color: var(--text-color);
`;

const ResultContainer = styled(motion.div)`
  margin-top: var(--spacing-lg);
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  background-color: ${props => props.passed ? 'rgba(75, 181, 67, 0.1)' : 'rgba(220, 53, 69, 0.1)'};
  border-left: 4px solid ${props => props.passed ? 'var(--success-color)' : 'var(--error-color)'};
`;

const Score = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
  color: var(--text-color);
`;

const FeedbackList = styled.ul`
  margin-left: var(--spacing-lg);
  color: var(--text-color);
  
  li {
    color: var(--text-color);
  }
  
  strong {
    color: var(--text-color);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: var(--background-light);
  border-radius: 4px;
  margin: var(--spacing-lg) 0;
  overflow: hidden;
`;

const Progress = styled.div`
  height: 100%;
  width: ${props => props.progress}%;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-lg);
`;

const quizQuestions = [
  {
    id: 1,
    question: "What keyword should you include in the subject line of an email containing PHI to enable encryption?",
    options: [
      { id: 'a', text: "SECURE" },
      { id: 'b', text: "PHI" },
      { id: 'c', text: "CONFIDENTIAL" },
      { id: 'd', text: "PRIVATE" }
    ],
    correctAnswer: 'b',
    explanation: "Include 'PHI' in the subject line to trigger automatic encryption for emails containing Protected Health Information."
  },
  {
    id: 2,
    question: "What is the proper way to manually encrypt an email in Outlook?",
    options: [
      { id: 'a', text: "Type 'ENCRYPT' at the beginning of the email body" },
      { id: 'b', text: "Use password-protected attachments" },
      { id: 'c', text: "Go to Options > Permission > Encrypt" },
      { id: 'd', text: "Send the email to security@company.com for encryption" }
    ],
    correctAnswer: 'c',
    explanation: "To manually encrypt an email in Outlook, go to Options > Permission and select 'Encrypt'."
  },
  {
    id: 3,
    question: "Which of the following is NOT a best practice for handling PHI in emails?",
    options: [
      { id: 'a', text: "Double-check the recipient's email address" },
      { id: 'b', text: "Use the minimum necessary PHI" },
      { id: 'c', text: "Include Protected Health Information (E.g Insurance ID, Social Security Number, etc.) in the subject line for visibility" },
      { id: 'd', text: "Always use encryption when sending PHI" }
    ],
    correctAnswer: 'c',
    explanation: "Never include PHI in the subject line, even if the email is encrypted. Subject lines should be descriptive but should not contain any Protected Health Information."
  },
  {
    id: 4,
    question: "What happens when you send an encrypted email to an external recipient?",
    options: [
      { id: 'a', text: "They need to register for a company account" },
      { id: 'b', text: "They receive a secure link and may need to use a one-time passcode" },
      { id: 'c', text: "They must have the same email encryption software" },
      { id: 'd', text: "They automatically receive a decryption key" }
    ],
    correctAnswer: 'b',
    explanation: "External recipients will receive a secure link and may need to use a one-time passcode that expires in 15 minutes."
  },
  {
    id: 5,
    question: "What should you do if you accidentally send PHI in an unencrypted email?",
    options: [
      { id: 'a', text: "Send a follow-up email asking the recipient to delete it" },
      { id: 'b', text: "Do nothing if the recipient has a need-to-know" },
      { id: 'c', text: "Immediately notify Security" },
      { id: 'd', text: "Recall the email using Outlook's recall feature" }
    ],
    correctAnswer: 'c',
    explanation: "If you accidentally send PHI in an unencrypted email, immediately notify Security."
  },
];

const QuizComponent = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showResults, setShowResults] = useState(false);
  
  const handleOptionSelect = (questionId, optionId) => {
    setSelectedOptions({
      ...selectedOptions,
      [questionId]: optionId
    });
  };
  
  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };
  
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  const handleReset = () => {
    setCurrentQuestion(0);
    setSelectedOptions({});
    setShowResults(false);
  };
  
  const calculateScore = () => {
    let correct = 0;
    quizQuestions.forEach(question => {
      if (selectedOptions[question.id] === question.correctAnswer) {
        correct++;
      }
    });
    return {
      score: correct,
      total: quizQuestions.length,
      percentage: Math.round((correct / quizQuestions.length) * 100)
    };
  };
  
  const isCurrentQuestionAnswered = () => {
    return !!selectedOptions[quizQuestions[currentQuestion].id];
  };
  
  const scoreData = calculateScore();
  const isPassing = scoreData.percentage >= 80;
  
  return (
    <QuizContainer>
      <QuizHeader>
        <QuizTitle>PHI Email Handling Quiz</QuizTitle>
        <div>Question {currentQuestion + 1} of {quizQuestions.length}</div>
      </QuizHeader>
      
      <QuizContent>
        {!showResults ? (
          <>
            <ProgressBar>
              <Progress progress={(currentQuestion / quizQuestions.length) * 100} />
            </ProgressBar>
            
            <Question>
              <QuestionText>{quizQuestions[currentQuestion].question}</QuestionText>
              <OptionsList>
                {quizQuestions[currentQuestion].options.map(option => (
                  <Option 
                    key={option.id}
                    selected={selectedOptions[quizQuestions[currentQuestion].id] === option.id}
                    onClick={() => handleOptionSelect(quizQuestions[currentQuestion].id, option.id)}
                  >
                    <OptionInput 
                      type="radio" 
                      id={`option-${option.id}`}
                      name={`question-${quizQuestions[currentQuestion].id}`}
                      checked={selectedOptions[quizQuestions[currentQuestion].id] === option.id}
                      onChange={() => handleOptionSelect(quizQuestions[currentQuestion].id, option.id)}
                    />
                    <OptionLabel htmlFor={`option-${option.id}`}>
                      {option.text}
                    </OptionLabel>
                  </Option>
                ))}
              </OptionsList>
            </Question>
            
            <Controls>
              <Button 
                variant="secondary" 
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
              >
                Previous
              </Button>
              <Button 
                onClick={handleNext}
                disabled={!isCurrentQuestionAnswered()}
              >
                {currentQuestion < quizQuestions.length - 1 ? 'Next' : 'Submit Quiz'}
              </Button>
            </Controls>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h3>Quiz Results</h3>
            <Score>Your Score: {scoreData.score}/{scoreData.total} ({scoreData.percentage}%)</Score>
            
            <ResultContainer passed={isPassing}>
              {isPassing ? (
                <div>
                  <p>
                    <strong>Congratulations!</strong> You've passed the PHI email handling quiz. You have demonstrated a good understanding of how to properly handle PHI in email communications.
                  </p>
                </div>
              ) : (
                <div>
                  <p>
                    <strong>Please review the material and try again.</strong> You need to score at least 80% to pass this quiz.
                  </p>
                </div>
              )}
            </ResultContainer>
            
            <div style={{ marginTop: 'var(--spacing-xl)' }}>
              <h4>Feedback:</h4>
              <FeedbackList>
                {quizQuestions.map(question => (
                  <li key={question.id} style={{ marginBottom: 'var(--spacing-md)' }}>
                    <strong>Question {question.id}:</strong> {question.question}
                    <div style={{ 
                      color: selectedOptions[question.id] === question.correctAnswer ? 
                        'var(--success-color)' : 'var(--error-color)',
                      margin: 'var(--spacing-sm) 0'
                    }}>
                      {selectedOptions[question.id] === question.correctAnswer ? 
                        '✓ Correct' : 
                        `✗ Incorrect (Correct answer: ${
                          question.options.find(o => o.id === question.correctAnswer).text
                        })`}
                    </div>
                    <div>{question.explanation}</div>
                  </li>
                ))}
              </FeedbackList>
            </div>
            
            <div style={{ textAlign: 'center', marginTop: 'var(--spacing-xl)' }}>
              <Button onClick={handleReset}>
                Take Quiz Again
              </Button>
            </div>
          </motion.div>
        )}
      </QuizContent>
    </QuizContainer>
  );
};

export default QuizComponent; 